import { dateToApiFormat } from '@/helpers/dateHelper';
import { OptimiserRequest, useSendOptimiseRequest } from '@/services/optimiser/useSendOptimseRequest';
import { useDaysInMonth } from '@/services/settings/useDates';
import { useTemplatesForLocation } from '@/services/shared/useTemplatesForLocation';
import { TemplateOption } from '@/types/template.types';
import { format } from 'date-fns';
import { FC, useCallback, useEffect, useState } from 'react';
import { GiAtom } from 'react-icons/gi';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageContainer } from '../../../components/page/PageContainer';
import { PageHeader } from '../../../components/page/PageHeader';
import { PageSizer, SizerMode } from '../../../components/page/PageSizer';
import { AccordionWizard } from '../../../components/shared/Accordion/AccordionWizard';
import { Button } from '../../../components/shared/button/Button';
import { TSCard } from '../../../components/shared/card/Card';
import strings from '../roster.translations';
import { OptimiserDateRange } from './OptimiserDateRange';
import OptimiserSelectTemplates from './OptimiserSelectTemplates';
import { useOptimiserSolverWizard } from './useOptimiserSolverWizard';

interface OptimiserSelection {
  date: Date;
  templateId?: string;
}

const buildRequestedDays = (
  startDate: Date,
  endDate: Date,
  currentRequests: OptimiserSelection[],
  templateOptions: TemplateOption[]
) => {
  /*
   * The requested days array needs to be set up whenever start and end date change.
   *
   * This function creates the array, keeping any data from the previous version of the array.
   */
  const newRequests: OptimiserSelection[] = [];
  let dateToAdd = new Date(startDate);

  // Take the first option which is classed as an acceptable default. Trust the template page not to create multiple defaults.
  const defaultOption = templateOptions.find(t => t.default);

  // Walk through the dates requested.
  while (dateToApiFormat(dateToAdd) <= dateToApiFormat(endDate)) {
    const newRequest: OptimiserSelection = {
      date: new Date(dateToAdd)
    };

    const existingRequest = currentRequests.find(r => dateToApiFormat(r.date) === dateToApiFormat(dateToAdd));

    if (existingRequest) {
      // If there's an existing request, respect that.
      newRequest.templateId = existingRequest.templateId;
    } else {
      // Fall back to the default.
      newRequest.templateId = defaultOption?.id;
    }

    newRequests.push(newRequest);

    dateToAdd.setUTCDate(dateToAdd.getUTCDate() + 1);
  }

  return newRequests;
};

export const OptimserPage: FC = () => {
  const { state } = useLocation();
  const date = state.selectedMonth;

  const returnLink = `/rosters/calendar/${dateToApiFormat(date)}`;

  const breadcrumbs = [
    { name: strings.rosterBreadcrumbTitle, to: returnLink },
    {
      name: strings.optimiseRosters.title,
      to: '/rosters/optimise'
    }
  ];

  const monthDates = useDaysInMonth(date);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isCurrentMonth = format(date, 'yyyy-MM') === format(today, 'yyyy-MM');
  const minStartDate = isCurrentMonth ? today : monthDates[0];

  const [startDate, setStartDate] = useState<Date | undefined>(minStartDate);
  const [endDate, setEndDate] = useState<Date | undefined>(monthDates[monthDates.length - 1]);

  const datesValid = endDate && startDate && endDate > startDate;

  const onChangeDates = useCallback(
    (newStart?: Date, newEnd?: Date) => {
      setStartDate(newStart);
      setEndDate(newEnd);
    },
    [setStartDate, setEndDate]
  );

  const [requestedDays, setRequestedDays] = useState<OptimiserSelection[]>([]);
  const allDaysRequested = requestedDays.every(r => r.templateId !== undefined);
  const { templateOptions } = useTemplatesForLocation();
  const { effectiveSolver, effectiveSolverLabel, solverWizardChoice } = useOptimiserSolverWizard();

  useEffect(() => {
    /*
     * Adjust the requested days array if start date or end date changes.
     */
    if (!datesValid) {
      // If either is not defined, completely wipe the array.
      setRequestedDays([]);
      return;
    }

    if (requestedDays.length > 0) {
      // If there's an existing array, check if the dates have changed.
      const firstDate = requestedDays[0].date;
      const lastDate = requestedDays[requestedDays.length - 1].date;

      const firstDateMatch = dateToApiFormat(firstDate) === dateToApiFormat(startDate);
      const lastDateMatch = dateToApiFormat(lastDate) === dateToApiFormat(endDate);

      if (firstDateMatch && lastDateMatch) {
        // If the dates haven't changed, no action is required.
        return;
      }
    }

    // Build a new requested days array.
    setRequestedDays(buildRequestedDays(startDate, endDate, requestedDays, templateOptions));
  }, [startDate, endDate, requestedDays, templateOptions, datesValid]);

  const navigate = useNavigate();

  const onOptimiseComplete = useCallback(() => {
    navigate(returnLink);
  }, []);

  const { sendOptimization, loading, error } = useSendOptimiseRequest(onOptimiseComplete);

  const wizardChoices = [
    {
      heading: strings.optimiseRosters.selectRosterDates,
      component: (
        <OptimiserDateRange
          startDate={startDate}
          endDate={endDate}
          changeDatesCallback={onChangeDates}
          minStartDate={minStartDate}
        />
      ),
      nextDisabled: !datesValid
    },
    {
      heading: strings.optimiseRosters.selectTemplates,
      component: <OptimiserSelectTemplates existingPattern={requestedDays} setPatternCallback={setRequestedDays} />,
      nextDisabled: !allDaysRequested
    },
    ...(solverWizardChoice ? [solverWizardChoice] : []),
    {
      heading: strings.optimiseRosters.createTabHeader,
      component: (
        <div className="flex flex-col gap-2">
          <p>{startDate && endDate && strings.optimiseRosters.submit.createPeriod(startDate!, endDate!)}</p>
          <p>{strings.optimiseRosters.submit.selectedSolver(effectiveSolverLabel)}</p>
          <p>{strings.optimiseRosters.submit.autofillTime}</p>
          <p>{strings.optimiseRosters.submit.start}</p>
          {error && <p className="text-red-500">{error}</p>}
        </div>
      )
    }
  ];

  return (
    <PageContainer
      customHeader={<PageHeader pageTitle={strings.cardTitle} breadcrumbs={breadcrumbs} icon={<GiAtom />} />}
    >
      <PageSizer className="flex flex-col w-full p-5" mode={SizerMode.PAGE}>
        <TSCard className="p-5">
          <AccordionWizard
            choices={wizardChoices}
            customSubmit={
              <Button
                disabled={!allDaysRequested}
                primary
                onClick={() => sendOptimization(requestedDays as OptimiserRequest[], effectiveSolver)}
                loading={loading}
              >
                {strings.optimiseRosters.submitText}
              </Button>
            }
          />
        </TSCard>
      </PageSizer>
    </PageContainer>
  );
};
